import { getUTM } from "./getUTM";

/**
 * Append a share attribution token to an already-built share URL.
 *
 * The token is appended as `?s=<shareId>` when no query string is present,
 * or as `&s=<shareId>` when one already exists (e.g. UTM params).
 *
 * @param url      - A share URL produced by `getTripShareUrl`.
 * @param shareId  - The unique token generated per share action.
 * @returns The URL with `s=<shareId>` appended.
 */
export const appendShareId = (url: string, shareId: string): string => {
	if (!url || !shareId) return url;
	const separator = url.includes("?") ? "&" : "?";
	return `${url}${separator}s=${encodeURIComponent(shareId)}`;
};

interface Organisation {
	_id?: string;
	slug?: string;
	name?: string;
}

/**
 * Generate a URL-friendly slug from a trip name
 * @param name - The trip name to slugify
 * @returns A lowercase, hyphenated slug safe for URLs
 */
export const generateSlug = (name: string): string => {
	if (!name) return "";
	return name
		.toLowerCase()
		.trim()
		.replace(/[^\w\s-]/g, "") // Remove special characters
		.replace(/\s+/g, "-") // Replace spaces with hyphens
		.replace(/-+/g, "-") // Collapse multiple hyphens
		.replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
};

/**
 * Whether the given user may share this trip. Public trips are shareable by anyone
 * (that's the point of PUBLIC); otherwise only the owner or a travel-party member can share.
 */
export const canShareTrip = (currentTrip: any, user?: any): boolean => {
	if (currentTrip?.privacyLevel === "PUBLIC") return true;
	const uid = user?.uid || user?.firebaseId;
	if (!uid) return false;
	const isOwner = currentTrip?.owner === uid || currentTrip?.author?.firebaseId === uid;
	const isPartyMember = currentTrip?.travelParty?.some((member: any) => member?.firebaseId === uid || member === uid || member?.username === user?.username);
	return Boolean(isOwner || isPartyMember);
};

export const getTripShareUrl = (currentTrip: any, user?: any, organisation?: Organisation) => {
	const tripName = currentTrip?.name;

	// B2B template: use /b/{orgSlug}/trip/{shortId}/{slug} format
	// Check for organisation from parameter, or from trip.organisation
	const org = organisation || currentTrip?.organisation;
	if (org?.slug && currentTrip?.shortId) {
		const slug = generateSlug(tripName);
		return `${window.origin}/b/${org.slug}/trip/${currentTrip.shortId}/${slug}${getUTM("businessTemplate", org.slug)}`;
	}
	// Legacy fallback for templates without shortId
	if (currentTrip?.isTemplate && org?.slug) {
		return `${window.origin}/b/${org.slug}/trip/${encodeURIComponent(tripName)}${getUTM("businessTemplate", org.slug)}`;
	}

	const isTripOwner = user ? currentTrip?.author?.firebaseId === user?.uid || currentTrip?.author?.firebaseId === user?.firebaseId : true;
	const authorUsername = currentTrip?.author?.username;

	// If we don't have the author username, use a simpler share URL
	if (!authorUsername || !tripName) {
		return `${window.origin}/share/${currentTrip?._id || ""}`;
	}

	const copyLink =
		isTripOwner && currentTrip?.author?.isCreator
			? `${window.origin}/${authorUsername}/trip/${encodeURIComponent(tripName)}${getUTM("creatorTrips", authorUsername)}`
			: `${window.origin}/${authorUsername}/trip/${encodeURIComponent(tripName)}${getUTM("userTripShare", authorUsername)}`;

	return copyLink;
};
